'use client';

import { useEffect, useState } from 'react';

import { proxyFetch } from '@ps/functions/utils/proxyFetch';

import type { PostViewInput } from '../functions/postViewModel';

interface State {
  input: PostViewInput | undefined;
  loading: boolean;
}

/**
 * Retry budget for the post-unlock re-fetch. The entitlement search index is
 * eventually-consistent: right after a pay/tip/subscribe unlock the post still
 * reads `restricted` for a beat, so a single fetch would keep the paywall up.
 * Mirrors upstream `reloadPost`'s `checkForUnlocked` loop (`maxAttempts` /
 * `attemptDelay`) — poll until the post comes back un-restricted or the budget
 * is spent (long enough to cover a tip's slower order settle, ~9s).
 */
const kMaxUnlockAttempts = 6;
const kUnlockAttemptDelayMs = 1500;

/**
 * Bio-scoped: re-fetch one post's slim {@link PostViewInput} via the server-side
 * posts proxy (`/api/bio/posts/by-id`), carrying the viewer's identity so an
 * entitled signed-in viewer gets the un-redacted post the anon-cached feed
 * couldn't show.
 *
 * Deliberately separate from the shared `useFetchPostById` (which stays on the
 * client `SearchApi` for messaging / community-channels) so the visitor bio
 * bundle ships no generated API client. A 404 (post not visible to the caller)
 * resolves to `undefined` — the caller keeps the server-rendered view.
 */
export function useFetchPostViewInput(
  storeId: string | undefined,
  postId: string | undefined,
  /**
   * Bump to force a re-fetch of the same `(storeId, postId)` — used after an
   * order-backed unlock (pay / tip), where entitlement changed server-side but
   * the fetch inputs didn't, so the effect wouldn't otherwise re-run.
   */
  reloadKey?: number,
  /**
   * When true, treat a still-`restricted` result as "the index hasn't caught up
   * yet" and retry (bounded by {@link kMaxUnlockAttempts}) until the post comes
   * back un-restricted. Set only AFTER an unlock event fired — a not-yet-entitled
   * probe (e.g. a pay post on first mount) leaves it false so it fetches once.
   */
  checkForUnlocked?: boolean,
): State {
  const [state, setState] = useState<State>({
    input: undefined,
    loading: true,
  });

  useEffect(() => {
    if (!storeId || !postId) {
      setState({ input: undefined, loading: false });
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setState({ input: undefined, loading: true });

    const params = new URLSearchParams({ storeId, postId });

    const attempt = (n: number): void => {
      proxyFetch<PostViewInput>(`/api/bio/posts/by-id?${params.toString()}`)
        .then(input => {
          if (cancelled) return;
          // Always surface the latest fetch — a crowd-tip's progress must update
          // even while the post stays restricted (the goal isn't met yet).
          setState({ input, loading: false });
          // Then keep polling for un-restriction: the index lags the order, so a
          // later attempt lifts the gate if the pay/tip/goal completed. (Mirrors
          // upstream `reloadPost({ checkForUnlocked, keepLatest })`.)
          if (checkForUnlocked && input?.restricted && n < kMaxUnlockAttempts) {
            timer = setTimeout(() => attempt(n + 1), kUnlockAttemptDelayMs);
          }
        })
        .catch(() => {
          if (cancelled) return;
          if (checkForUnlocked && n < kMaxUnlockAttempts) {
            timer = setTimeout(() => attempt(n + 1), kUnlockAttemptDelayMs);
            return;
          }
          setState({ input: undefined, loading: false });
        });
    };
    attempt(1);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [storeId, postId, reloadKey, checkForUnlocked]);

  return state;
}
